import { Button } from '@atoms'
import { toast, type ToastOptions } from '@stores/toastStore'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<ToastOptions> = {
  description: { type: 'string', description: "Detail the title can't hold. Rendered under it, in a lighter weight." },
  duration: {
    type: 'number',
    default: '5000',
    description: 'Milliseconds before auto-dismiss. Infinity keeps it until the user closes it.',
  },
  action: {
    type: '{ label: string; onClick: () => void }',
    description: 'One link-style button under the text, such as Undo. Clicking it runs onClick and closes the toast.',
  },
}

function Fire({ label, fire }: { label: string; fire: () => void }) {
  return (
    <Button rank="secondary" size="sm" onClick={fire}>
      {label}
    </Button>
  )
}

const FLASH_KEYS: [string, string][] = [
  ['notice', 'success'],
  ['success', 'success'],
  ['alert', 'error'],
  ['danger', 'error'],
  ['warning', 'warning'],
]

function FlashTable() {
  return (
    <table className="tw:w-full tw:max-w-md tw:bg-white tw:text-left tw:text-sm">
      <thead>
        <tr className="tw:border-b tw:border-gray-cool-200 tw:text-xs tw:uppercase tw:tracking-wide tw:text-gray-cool-500">
          <th className="tw:py-2 tw:font-medium">Flash key</th>
          <th className="tw:py-2 tw:font-medium">Toast type</th>
        </tr>
      </thead>
      <tbody>
        {FLASH_KEYS.map(([key, type]) => (
          <tr key={key} className="tw:border-b tw:border-gray-cool-100">
            <td className="tw:py-2 tw:font-mono tw:text-gray-cool-900">{key}</td>
            <td className="tw:py-2 tw:font-mono tw:text-gray-cool-600">{type}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const doc: ComponentDoc<ToastOptions> = {
  slug: 'toasts',
  name: 'Toasts',
  tier: 'pattern',
  description:
    'A short, self-dismissing confirmation in the top-right corner. Fire one from anywhere with the toast helper; the single Toaster mounted in AppProviders renders it, so pages never mount their own. Every button below fires a real toast.',
  importStatement: "import { toast } from '@stores/toastStore'",
  sourcePath: 'app/frontend/stores/toastStore.ts',
  props,
  examples: [
    {
      title: 'Types',
      note: 'One function per type. The title is required and should read as a finished sentence fragment: what just happened.',
      render: () => (
        <>
          <Fire label="Success" fire={() => toast.success('Group saved')} />
          <Fire label="Error" fire={() => toast.error('Export failed')} />
          <Fire label="Info" fire={() => toast.info('Import queued')} />
          <Fire label="Warning" fire={() => toast.warning('Event is over capacity')} />
        </>
      ),
      code: [
        "toast.success('Group saved')",
        "toast.error('Export failed')",
        "toast.info('Import queued')",
        "toast.warning('Event is over capacity')",
      ].join('\n'),
    },
    {
      title: 'With description',
      render: () => (
        <Fire
          label="With description"
          fire={() => toast.success('Export requested', { description: "We'll email a download link to you@example.com." })}
        />
      ),
      code: "toast.success('Export requested', {\n  description: \"We'll email a download link to you@example.com.\",\n})",
    },
    {
      title: 'Longer duration',
      note: 'The default is 5 seconds. Hover, focus or leaving the tab pauses the timer; swiping right or the close button dismisses early.',
      render: () => (
        <Fire label="Longer duration" fire={() => toast.info('Syncing 1,204 supporters', { duration: 15_000 })} />
      ),
      code: "toast.info('Syncing 1,204 supporters', { duration: 15_000 })",
    },
    {
      title: 'Sticky until closed',
      render: () => (
        <Fire label="Sticky until closed" fire={() => toast.error('Connection lost', { duration: Infinity })} />
      ),
      code: "toast.error('Connection lost', { duration: Infinity })",
    },
    {
      title: 'With an action',
      note: 'For undoing what the user just did, without a confirm dialog first.',
      render: () => (
        <Fire
          label="With an action"
          fire={() => toast.success("Maria's callback marked as done", { action: { label: 'Undo', onClick: () => {} } })}
        />
      ),
      code: "toast.success(\"Maria's callback marked as done\", {\n  action: { label: 'Undo', onClick: restore },\n})",
    },
    {
      title: 'Rails flash',
      note: 'Server-side flash needs no frontend code. inertia_rails puts it on page.flash, the Inertia client fires a flash event on load and after every visit, and the entrypoint turns each key into a toast.',
      render: () => <FlashTable />,
      code: "redirect_to group_path(@group), notice: t('.flash.saved')\nredirect_back_or_to root_path, alert: t('.flash.denied')",
    },
  ],
  usage: {
    do: [
      'An action the user just took has finished and nothing else needs doing.',
      'A background job was queued and the user should know where the result will land.',
      'A request failed in a way the user cannot fix from the current form.',
    ],
    dont: [
      'Validation errors. Use AppErrorsAlert next to the form so the message stays until fixed.',
      'Anything that needs a decision. Use a dialog.',
      'State the page already shows, such as a row appearing in a list.',
      'Several toasts for one action. One toast, one outcome.',
    ],
  },
  a11y: [
    'Built on Radix Toast, so each toast is announced as a status region when it appears. Success and info announce politely; error and warning interrupt.',
    'The close button is labelled and keyboard reachable; F8 moves focus to the toast viewport.',
    'Hover, focus and tab switches pause auto-dismiss. Use duration: Infinity when the message must be read.',
    'Colour is paired with an icon per type, so the type is never colour-only.',
  ],
}

export default doc
